<template>
  <div class="session-expire-warning" v-if="warningZone">
    <div>
    <span>Your session has </span>
    <span v-if="message">been inactive for more than
      {{ displayIdleForWarningTime }} minutes. Please continue the work or save the changes.
      If not, you will be logged out automatically in {{ message }}.
    </span>
    <span v-else>expired.</span>
  </div>
  </div>
</template>

<script>
import { tokenStorage, parseJwt } from '../shared/storage-service';
import appConfig, { APP_DATA } from '../app.config';
import AuthHelper from '../modules/auth/components/auth-helper';

const MAX_IDLE_TIME = appConfig.maxIdleTime * 60; // in seconds
const WARNING_STARTS_IN = appConfig.userSessionWarningStartsIn * 60; // in seconds
const REFRESH_REQUIRED_BEFORE = appConfig.tokenRefresgRequiredBefore * 60; // in seconds

export default {
  name: 'AppUserSessionNotifier',
  created() {
  },
  mounted() {
    this.startMonitoring();
  },
  destroyed() {
    this.stopMonitoring();
  },
  data() {
    return {
      events: ['click', 'mousemove', 'mousedown', 'scfroll', 'keypress', 'load'],
      countDownRef: null,
      warningZone: false,
      message: null,
      // if less than one minute, add seconds to display
      displayIdleForWarningTime: Math.floor(WARNING_STARTS_IN / 60),
    };
  },
  computed: {
    isUserLogged() {
      return !!this.$store.getters.loggedUser;
    },
  },
  methods: {
    resetIdleTime() {
      APP_DATA.idleTime = 0;
      this.warningZone = false;
    },
    startMonitoring() {
      if (this.isUserLogged) {
        this.stopMonitoring();
        this.events.forEach((event) => {
          window.addEventListener(event, this.resetIdleTime);
        }, this);
        this.countDownRef = this.startCountDown();
      }
    },
    stopMonitoring() {
      this.setCountDownOff(this.countDownRef);
      this.resetIdleTime({ type: 'code' });
      this.events.forEach((event) => {
        window.removeEventListener(event, this.resetIdleTime);
      }, this);
    },
    startCountDown() {
      const timeInterval = 1; // <--seconds ---//

      return setInterval(() => {
        APP_DATA.idleTime += timeInterval;

        if (APP_DATA.idleTime >= MAX_IDLE_TIME) {
          this.setCountDownOff(this.countDownRef);
          AuthHelper.signUserOut().catch(() => {
            // console.error('Signout Error', error);
          }).finally(() => {
            // console.log('Session timed out. Redirecting to Home.');
            const redirectPath = '/session-expired';
            if (this.$route.path !== redirectPath) this.$router.push(redirectPath);
          });
          return;
        }

        try {
          const now = new Date().getTime();
          if (APP_DATA.idleTime <= timeInterval) { // only broadcasts in the fist iteration
            const lastRecordedActiveTimeData = tokenStorage.getLastRecordedActiveTimeData();
            if (lastRecordedActiveTimeData) {
              const lastRecordedActiveTime = parseInt(lastRecordedActiveTimeData, 10);

              // ----- the time duration since the last idle data is recorded in storage + 1ms,
              //  so the active tab can keep broadcasting in every iteration -----//
              const timeSinceLastBroadcasted = (now - (lastRecordedActiveTime + 1)) / 1000;
              const myIdleTime = (APP_DATA.idleTime === 0) ? timeInterval : APP_DATA.idleTime;

              if (myIdleTime < timeSinceLastBroadcasted) {
                tokenStorage.setLastRecordedActiveTimeData(now);
              }
            } else tokenStorage.setLastRecordedActiveTimeData(now);
          }

          // ---- Show warning banner if in Warning Zone ---------//
          if (APP_DATA.idleTime >= WARNING_STARTS_IN) this.showSessionExpiringWarning();
          else this.hideSessionExpiringWarning();

          // ----- refresh tokens if REFRESH_REQUIRED_BEFORE zone is passing ----//
          this.refreshTokenIfExpiringSoon(now);
        } catch (err) {
          // console.log(err);
        }
      }, timeInterval * 1000);
    },
    showSessionExpiringWarning() {
      const timeToExpire = MAX_IDLE_TIME - APP_DATA.idleTime;
      const minutes = Math.floor(timeToExpire / 60); // get minutes
      const seconds = Math.floor(timeToExpire % 60); // get seconds
      this.message = `${minutes}m ${seconds}s`;
      this.warningZone = true;
    },
    hideSessionExpiringWarning() {
      this.warningZone = false;
      this.message = null;
    },
    refreshTokenIfExpiringSoon(now) {
      const timeLeftInToken = Math.floor(
        // eslint-disable-next-line function-paren-newline
        (this.getTokenExpirationTime(tokenStorage.getRefreshToken()) - now) / 1000);
      // ---- adding random 0-30 seconds
      // so only one tab will go in to make the refresh token request -----//
      const timeLeftToRequestToken = timeLeftInToken + Math.floor(Math.random() * 30);
      if (timeLeftToRequestToken <= REFRESH_REQUIRED_BEFORE) {
        AuthHelper.refreshTokensIfNotRequestedAlready();
      }
    },
    getTokenExpirationTime(jwtToken) {
      const jwt = parseJwt(jwtToken);
      const refreshExpDate = new Date(jwt.exp * 1000);
      return refreshExpDate.getTime();
    },
    setCountDownOff(interval) {
      if (interval) {
        clearInterval(interval);
      }
      this.warningZone = false;
    },
  },
  watch: {
    isUserLogged(newVal) {
      if (newVal) this.startMonitoring();
      else this.stopMonitoring();
    },
  },
};
</script>
<style >
.session-expire-warning {
    width: 100% !important;
    height: 86px;
    background: #d56301;
    position: absolute;
    z-index: 1001;
    color: white;
    text-align: center;
    padding: 34px;
}
</style>
